import { useTranslation } from '../../../i18n';
import React from 'react';
import { Skeleton, SkeletonAvatar, SkeletonRegion } from '@parsina/framework';
import { C } from '../../../constants/theme';
import { CT } from '../../../constants/unifiedTheme';
import { bffQuery } from '../../../lib/bffClient';
import { fmtJalaliLong, dayDateOf, slotInstant } from '../helpers';
import { addDaysIso, clinicTodayIso } from '../../../lib/clinicZone';
import { SCHEDULED_FOR_DAY, localDayRange, providerBookings, selectableSlots } from './useSelectableSlots';

// "اولین نوبت" (first available slot) picker, shared by the booking modals.
// The preview is advisory only — the BFF re-resolves the slot when the booking
// is submitted, so a slot shown here can still be taken by someone else first.

const FIRST_AVAILABLE_SLOT = `
  query FirstAvailableSlot($doctorId: String!, $fromDateTime: String!, $scope: FirstSlotScope!) {
    firstAvailableSlot(doctorId: $doctorId, fromDateTime: $fromDateTime, scope: $scope) {
      dateTime
      isAvailable
    }
  }
`;

const HandIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="13" height="13">
    <path d="M13 1.07V9h7c0-4.08-3.05-7.44-7-7.93zM4 15c0 4.42 3.58 8 8 8s8-3.58 8-8v-4H4v4zM11 1.07C7.05 1.56 4 4.92 4 9h7V1.07z" />
  </svg>
);
const BoltIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="13" height="13">
    <path d="M7 2v11h3v9l7-12h-4l4-8z" />
  </svg>
);
const RocketIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="13" height="13">
    <path d="M12 2.5s4.5 2 4.5 8c0 2.2-.7 4-1.5 5.2V18h-6v-2.3C8.2 14.5 7.5 12.7 7.5 10.5c0-6 4.5-8 4.5-8zm0 4a1.75 1.75 0 100 3.5 1.75 1.75 0 000-3.5zM9 19h6v1.2c0 .5-.4.8-.8.8h-4.4c-.4 0-.8-.3-.8-.8V19z" />
  </svg>
);
const ClockIcon = ({ size = 18, color = 'white' }) => (
  <svg viewBox="0 0 24 24" fill={color} width={size} height={size}>
    <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
  </svg>
);

// Auto-pick first, manual last: booking the earliest free slot is what the desk does
// all day, and the mode that opens by default belongs at the head of the row rather
// than in the middle of it. The order is the same everywhere the selector is used.
export const SLOT_MODES = (t) => ([
  { key: 'firstOfDay', label: t('doctors.slotModes.firstOfDay'), todayLabel: t('doctors.slotModes.firstToday'), Icon: BoltIcon },
  { key: 'firstAny',   label: t('doctors.slotModes.firstAny'),    Icon: RocketIcon },
  { key: 'manual',     label: t('doctors.slotModes.manual'),        Icon: HandIcon },
]);

// A booking offers two modes, not three — on the desk as on the phone (PM-0030).
// «اولین نوبت پزشک» searches forward across days; the desk books a walk-up for
// today or picks a time, and a third tab only crowds the row. The empty-day card
// still reaches the doctor's next slot, by moving the day to it (see `onUseNext`).
export const BOOKING_SLOT_MODES = ['firstOfDay', 'manual'];

export function slotModeScope(mode) {
  return mode === 'firstAny' ? 'ANY' : 'DAY';
}

// "۰۹:۳۰" from an epoch-ms string
export function fmtSlotTime(dateTimeMs, formatTime) {
  return formatTime(Number(dateTimeMs));
}

// "سه‌شنبه، ۲۳ تیر ۱۴۰۵، ساعت ۰۹:۳۰" — used for the success toast
export function fmtSlotLabel(dateTimeMs, t, formatDate, formatTime) {
  const d = new Date(Number(dateTimeMs));
  if (isNaN(d.getTime())) return '';
  return t('doctors.slot.dateAtTime', {
    date: fmtJalaliLong(d, formatDate),
    time: fmtSlotTime(dateTimeMs, formatTime),
  });
}

// "امروز ساعت ۰۹:۳۰" / "سه‌شنبه، ۲۳ تیر ۱۴۰۵، ساعت ۰۹:۳۰"
function fmtSlotWhen(dateTimeMs, t, formatDate, formatTime) {
  const d = new Date(Number(dateTimeMs));
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const tomorrow = new Date(now); tomorrow.setDate(tomorrow.getDate() + 1);
  const prefix = sameDay
    ? t('common.dates.today')
    : d.toDateString() === tomorrow.toDateString()
      ? t('common.dates.tomorrow')
      : fmtJalaliLong(d, formatDate);
  return t('doctors.slot.dateAtTime', { date: prefix, time: fmtSlotTime(dateTimeMs, formatTime) });
}

// Debounced preview of the slot an auto-pick booking would take.
// Returns { slot, loading, error }; slot is null when nothing is free.
export function useFirstAvailableSlot(enabled, doctorId, fromDateTime, scope) {
  const [state, setState] = React.useState({ slot: null, loading: false, error: false });

  React.useEffect(() => {
    if (!enabled || !doctorId) {
      setState({ slot: null, loading: false, error: false });
      return;
    }
    let cancelled = false;
    setState({ slot: null, loading: true, error: false });
    const t = setTimeout(async () => {
      try {
        const data = await bffQuery(FIRST_AVAILABLE_SLOT, { doctorId, fromDateTime, scope });
        if (!cancelled) setState({ slot: data?.firstAvailableSlot || null, loading: false, error: false });
      } catch {
        if (!cancelled) setState({ slot: null, loading: false, error: true });
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [enabled, doctorId, fromDateTime, scope]);

  return state;
}

// ── «اولین نوبت» for a PERFORMER — a service session's first free slot ────────
//
// `firstAvailableSlot` is a doctor's: the scheduling service walks his shift at his
// VISIT length. A service session is a slot of the DECLARATION's length, and its
// performer may be a coworker with no shift at all — so the search is made here,
// from the same three rules the manual grid draws with (`selectableSlots`): the
// shift (or opening hours), the performer's own diary, and the clock. Advisory,
// like the doctor's: the server still refuses the slot if somebody took it first.
//
// DAY looks at one day. ANY walks forward from now, a day per request, and stops
// at the first day with something free — usually the first or second request.
const FIRST_ANY_HORIZON_DAYS = 30;

async function firstFreeOn(day, { providerId, presences, shiftless, timeSlots, slotMin, nowMs }) {
  const { startTime, endTime } = localDayRange(day);
  const data = await bffQuery(SCHEDULED_FOR_DAY, { startTime, endTime });
  const booked = providerBookings(data?.scheduledAppointments, providerId, slotMin);
  const [first] = selectableSlots({ timeSlots, presences, shiftless, selectedDate: day, slotMin, booked, nowMs });
  if (!first) return null;
  return { dateTime: String(slotInstant(day, first.h, first.m)), isAvailable: true };
}

export function useFirstPerformerSlot({ enabled, providerId, presences, shiftless, timeSlots, slotMin, fromDateTime, scope }) {
  const [state, setState] = React.useState({ slot: null, loading: false, error: false });
  // Presence rows and the grid are arrays a parent may rebuild each render; the
  // search re-runs on what they SAY, not on their identity.
  const presenceKey = JSON.stringify(presences || []);
  const gridKey = `${timeSlots?.[0]?.label || ''}-${timeSlots?.length || 0}-${slotMin}`;

  React.useEffect(() => {
    if (!enabled || !providerId) {
      setState({ slot: null, loading: false, error: false });
      return;
    }
    let cancelled = false;
    setState({ slot: null, loading: true, error: false });
    const t = setTimeout(async () => {
      try {
        const nowMs = Date.now();
        const opts = { providerId, presences, shiftless, timeSlots, slotMin, nowMs };
        let slot = null;
        if (scope === 'DAY') {
          slot = await firstFreeOn(new Date(Number(fromDateTime)), opts);
        } else {
          // Day by day on the CLINIC's calendar (PM-0088), from its «امروز».
          const today = clinicTodayIso(nowMs);
          for (let i = 0; i < FIRST_ANY_HORIZON_DAYS && !slot && !cancelled; i += 1) {
            slot = await firstFreeOn(dayDateOf(addDaysIso(today, i)), opts);
          }
        }
        if (!cancelled) setState({ slot, loading: false, error: false });
      } catch {
        if (!cancelled) setState({ slot: null, loading: false, error: true });
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, providerId, presenceKey, shiftless, gridKey, fromDateTime, scope]);

  return state;
}

// The performer's counterpart of `useNextAvailableSlot`: from now, any day.
export function useNextPerformerSlot(enabled, opts) {
  const anchor = React.useMemo(() => String(Date.now()), [enabled, opts.providerId]);
  return useFirstPerformerSlot({ ...opts, enabled, fromDateTime: anchor, scope: 'ANY' });
}

// The «نزدیک‌ترین نوبت» probe behind the empty-day card: the doctor's first free slot
// from NOW, whatever day it lands on. It only runs once the day-scoped search has come
// back empty, so the common path still makes exactly one request.
//
// The anchor is frozen for the life of one probe. Recomputing Date.now() every render
// would change the effect's dependency on every render and re-query forever.
export function useNextAvailableSlot(enabled, doctorId) {
  const anchor = React.useMemo(() => String(Date.now()), [enabled, doctorId]);
  return useFirstAvailableSlot(enabled, doctorId, anchor, 'ANY');
}

const styles = `
  @keyframes fsIn      { from { opacity:0; transform:translateY(-4px) } to { opacity:1; transform:none } }
  .fs-mode {
    flex:1; display:flex; align-items:center; justify-content:center; gap:5px;
    padding:9px 4px; border:none; cursor:pointer;
    font-family:Meshkat, sans-serif; font-size:12px; font-weight:700;
    background:white; color:${C.textSecondary};
    transition:background .16s, color .16s;
  }
  .fs-mode:not(.is-on):hover { background:#f1f5f9; color:${C.textPrimary}; }
  .fs-mode.is-on { background:${C.navy}; color:white; }
  .fs-card { animation:fsIn .2s ease-out both; }
`;

// `dayIsToday` names the day-scoped mode after the day it will actually search:
// «اولین نوبت امروز» while the date picker sits on today (the case the desk opens in),
// «اولین نوبت این روز» once they have navigated to another day — where "امروز" would
// be a lie about which day the slot comes from.
// `modes` narrows the row (bookings pass BOOKING_SLOT_MODES); the order stays SLOT_MODES'.
// `labels` renames a mode for the caller's subject — «اولین نوبت انجام‌دهنده» when the
// person being booked is a coworker, where «پزشک» would name the wrong person.
export function SlotModeSelector({ mode, onChange, dayIsToday = false, modes = null, labels = null }) {
  const { t } = useTranslation();
  const shown = SLOT_MODES(t)
    .filter(m => !modes || modes.includes(m.key))
    .map(m => (labels?.[m.key] ? { ...m, label: labels[m.key], todayLabel: undefined } : m));
  return (
    <>
      <style>{styles}</style>
      <div style={{ display: 'flex', border: `1.5px solid ${C.border}`, borderRadius: 10, overflow: 'hidden', marginBottom: 12 }}>
        {shown.map(({ key, label, todayLabel, Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            aria-pressed={mode === key}
            className={`fs-mode${mode === key ? ' is-on' : ''}`}>
            <Icon />
            {dayIsToday && todayLabel ? todayLabel : label}
          </button>
        ))}
      </div>
    </>
  );
}

// A collapsed group of booking options: one quiet row that says what
// is behind it, expanded only when the desk needs it. The default answer — «اولین
// نوبت امروز» — is already on screen without it.
// `closedGap` spaces it from what follows while folded; open, its children carry their own.
export function OptionsDisclosure({ open, onToggle, showLabel, hideLabel, testId, closedGap = 0, children }) {
  return (
    <div style={{ marginBottom: open ? 0 : closedGap }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        data-testid={testId}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          minHeight: 40, padding: '8px 12px', borderRadius: 10,
          border: `1.5px dashed ${C.border}`, background: 'white', cursor: 'pointer',
          fontFamily: 'Meshkat, sans-serif', fontSize: 12.5, fontWeight: 700,
          color: C.navy, textAlign: 'start',
        }}>
        <span>{open ? hideLabel : showLabel}</span>
        <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" aria-hidden="true"
          style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .16s' }}>
          <path d="M7 10l5 5 5-5z" />
        </svg>
      </button>
      {open ? <div style={{ marginTop: 12 }}>{children}</div> : null}
    </div>
  );
}

function Box({ tone, children }) {
  const tones = {
    amber: { bg: CT.amberLight, border: 'rgba(245,158,11,0.30)', color: CT.warnInk },
    red:   { bg: C.redLight, border: 'rgba(217,48,37,0.25)', color: C.red }
  }[tone];
  return (
    <div className="fs-card" style={{
      padding: '11px 14px', background: tones.bg, border: `1px solid ${tones.border}`,
      borderRadius: 12, fontSize: 12.5, color: tones.color, fontWeight: 600, lineHeight: 1.6
    }}>
      {children}
    </div>
  );
}

// What the desk sees when the chosen day has nothing left: the reason, and — for a
// day-scoped search — the next slot the doctor actually has, with a button that takes
// it. Telling a receptionist «روز دیگری انتخاب کنید» and leaving them to hunt for that
// day one dropdown at a time is the step this removes.
function EmptyDay({ dayIsToday, next, onUseNext }) {
  const { t, formatDate, formatTime } = useTranslation();
  const why = dayIsToday
    ? t('doctors.slots.noneLeftToday')
    : t('doctors.slots.noneThisDay');

  return (
    <Box tone="amber">
      <div>⚠️ {why}</div>
      {next.loading && (
        <div style={{ marginTop: 7, fontWeight: 600, opacity: 0.85 }}>{t('doctors.slots.finding')}</div>
      )}
      {!next.loading && next.slot && (
        <div style={{ marginTop: 9, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span>{t('doctors.slots.nextIs')} <b>{fmtSlotWhen(next.slot.dateTime, t, formatDate, formatTime)}</b></span>
          <button
            type="button"
            onClick={() => onUseNext?.(next.slot)}
            style={{
              padding: '5px 12px', borderRadius: 8, border: `1.5px solid ${C.navy}`,
              background: 'white', color: C.navy, fontSize: 12, fontWeight: 700,
              fontFamily: 'Meshkat, sans-serif', cursor: 'pointer'
            }}>
            {t('doctors.slots.bookThisOne')}
          </button>
        </div>
      )}
      {!next.loading && !next.slot && !next.error && (
        <div style={{ marginTop: 7 }}>{t('doctors.slots.noneIn30Days')}</div>
      )}
    </Box>
  );
}

export function FirstSlotPreview({ mode, loading, error, slot, dayIsToday, next, onUseNext }) {
  const { t } = useTranslation();
  const { formatDate, formatTime } = useTranslation();
  if (loading) {
    // The slot card's own shape (38px disc, a when-line and a sub-line), so the
    // found slot lands in the same box.
    return (
      <SkeletonRegion
        label={t('common.state.loading')}
        className="fs-card"
        style={{ display: 'flex', alignItems: 'center', gap: 11, background: 'white', border: `1.5px solid ${C.border}`, borderRadius: 12, padding: 12 }}>
        <SkeletonAvatar size={38} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 7 }}>
          <Skeleton width="58%" height={13} radius={6} />
          <Skeleton width="38%" height={10} radius={6} />
        </div>
      </SkeletonRegion>
    );
  }

  if (error) {
    return <Box tone="red">{t('errors.doctors.firstSlotFailed')}</Box>;
  }

  if (!slot) {
    if (mode !== 'firstOfDay') {
      return <Box tone="amber">{t('doctors.slots.noneIn30DaysWarning')}</Box>;
    }
    return (
      <EmptyDay
        dayIsToday={dayIsToday}
        next={next || { loading: false, slot: null, error: false }}
        onUseNext={onUseNext}
      />
    );
  }

  const d = new Date(Number(slot.dateTime));
  return (
    <div className="fs-card" style={{
      display: 'flex', alignItems: 'center', gap: 12,
      background: 'linear-gradient(135deg, color-mix(in srgb, var(--p-navy) 8%, transparent), color-mix(in srgb, var(--p-navy) 2%, transparent))',
      border: `1.5px solid ${C.navy}`, borderRadius: 12, padding: '12px 14px'
    }}>
      <div style={{
        width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
        background: `linear-gradient(135deg, ${C.navyDark}, ${C.navy})`,
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <ClockIcon />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, color: C.textMuted, marginBottom: 3 }}>
          {t('doctors.slots.firstFree')}
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 18, fontWeight: 800, color: C.navy, letterSpacing: 0.3 }}>
            {fmtSlotTime(slot.dateTime, formatTime)}
          </span>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: C.textSecondary }}>
            {fmtJalaliLong(d, formatDate)}
          </span>
        </div>
        <div style={{ fontSize: 10.5, color: C.textMuted, marginTop: 4 }}>
          {t('doctors.slots.exactAtBooking')}
        </div>
      </div>
    </div>
  );
}
